import { useQuery } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

import { searchL3Memories } from '../api/memory.client.js';

/**
 * Семантический поиск по L3 — это чтение, поэтому он модель как query, а не как
 * mutation.
 *
 * Мутация ломалась под `<StrictMode>` (см. `main.tsx`): авто-запуск из
 * `useEffect` вызывал `mutateAsync`, StrictMode в dev размонтировал и смонтировал
 * компонент заново, наблюдатель мутации отвязывался от летящего запроса, а
 * защита от повторного запуска (ref) не давала запустить его снова — панель
 * навсегда оставалась в `isPending`, а кнопка «Найти» — в loading. У query нет
 * наблюдателя, которого можно потерять: состояние живёт в кэше по ключу, и
 * повторный маунт просто подписывается на уже идущий запрос.
 *
 * Ключ — под общим корнем `memory`, как у `useRecentMemories`, поэтому правка и
 * забывание записи (`useInvalidateMemory`) обновляют и выдачу поиска.
 */
export function useMemorySearch(query: string, limit = 10) {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const normalized = query.trim();

  return useQuery({
    queryKey: ['memory', 'search', workspaceId, normalized, limit],
    queryFn: () => searchL3Memories(normalized, limit),
    // Пустая фраза — это «поиск не запускали», а не «искать нечего».
    enabled: scope !== null && normalized.length > 0,
    // Поиск может быть недоступен навсегда (501, пока провайдер эмбеддингов не
    // подключён): повтор не поможет, а ошибку показывает сама панель.
    retry: false,
  });
}
